import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { Avatar } from '@/components/ui';
import { prepareCall } from './prepare';

const RING_TIMEOUT_MS = 45_000;

export function IncomingCallModal() {
  const incoming = useUi(s => s.incomingCall);
  const setIncoming = useUi(s => s.setIncomingCall);
  const setActiveCall = useUi(s => s.setActiveCall);
  const toast = useUi(s => s.toast);

  // t437u: warm LiveKit (and load the call screen) while it rings, so Accept connects sooner.
  useEffect(() => { if (incoming) prepareCall(); }, [incoming?.call_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!incoming) return;
    const left = RING_TIMEOUT_MS - (Date.now() - incoming.received_at);
    const t = setTimeout(() => setIncoming(null), Math.max(0, left));
    // In the background or closed, the call.incoming Web Push rings the device instead (t253u).
    return () => clearTimeout(t);
  }, [incoming, setIncoming]);

  if (!incoming) return null;

  const accept = async () => {
    setIncoming(null);
    if (useUi.getState().activeCall) { toast('Leave your current call first', 'error'); return; }
    prepareCall(); // no-op when the ring already warmed it
    try { setActiveCall(await api.joinCall(incoming.call_id)); } catch (e) { toast(`Couldn't join: ${errorMessage(e)}`, 'error'); }
  };
  const decline = async () => {
    setIncoming(null);
    await api.declineCall(incoming.call_id).catch(() => undefined);
  };

  return createPortal(
    <div className="fixed inset-x-0 top-[max(1.5rem,env(safe-area-inset-top))] z-[70] flex justify-center px-3">
      <div role="alertdialog" aria-label={`Incoming ${incoming.media} call from ${incoming.from_name}`} data-testid="incoming-call"
        className="flex w-full max-w-[24rem] items-center gap-4 rounded-2xl bg-slate-900 p-4 text-white shadow-2xl ring-1 ring-white/10">
        <div className="relative">
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/40" />
          <Avatar size={48} name={incoming.from_name} id={incoming.from_user_id} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{incoming.from_name}</div>
          <div className="truncate text-sm text-white/60">Incoming {incoming.media} call…</div>
        </div>
        <button onClick={() => void decline()} className="rounded-full bg-rose-600 p-3 hover:bg-rose-500" title="Decline" aria-label="Decline" data-testid="incoming-call-decline"><PhoneOff className="h-5 w-5" /></button>
        <button onClick={() => void accept()} className="rounded-full bg-emerald-600 p-3 hover:bg-emerald-500" title="Accept" aria-label="Accept" data-testid="incoming-call-accept">{incoming.media === 'video' ? <Video className="h-5 w-5" /> : <Phone className="h-5 w-5" />}</button>
      </div>
    </div>,
    document.body,
  );
}
